import React, { forwardRef } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain, faChartLine, faEye, faLock, faShieldAlt, faCloud, faWifi,
  faUserShield, faHeartbeat, faLanguage, faNetworkWired, faDatabase,
  faAtom, faCube, faBolt, faMicrochip, faPlug, faCarBattery, faCogs,
  faIndustry, faRobot, faCog, faBuilding, faMountain, faSeedling, faRoad,
  faHardHat, faWater, faCity, faHome, faDraftingCompass, faChargingStation
} from "@fortawesome/free-solid-svg-icons";

const ServiceSection = forwardRef((props, ref) => {
  const cseProjects = [
    { name: "Artificial Intelligence", icon: faBrain },
    { name: "Data Analysis", icon: faChartLine },
    { name: "Computer Vision", icon: faEye },
    { name: "Cryptography", icon: faLock },
    { name: "Cybersecurity", icon: faShieldAlt },
    { name: "Internet of Things", icon: faWifi },
    { name: "Data Mining", icon: faDatabase },
    { name: "Information Security", icon: faUserShield },
    { name: "Healthcare Applications", icon: faHeartbeat },
    { name: "Natural Language Processing", icon: faLanguage },
    { name: "Networking", icon: faNetworkWired },
    { name: "Cloud Computing", icon: faCloud },
    { name: "Quantum Computing", icon: faAtom },
    { name: "Blockchain", icon: faCube }
  ];

  const eeeProjects = [
    { name: "Renewable Energy Sources", icon: faSeedling },
    { name: "Control System", icon: faCogs },
    { name: "Signal Processing", icon: faChartLine },
    { name: "Power Quality Analysis", icon: faBolt },
    { name: "VLSI in Power Electronics", icon: faMicrochip },
    { name: "Power Electronics", icon: faPlug },
    { name: "Electric Power System", icon: faCarBattery },
    { name: "Electrical Machines", icon: faCog },
    { name: "Microgrid Implementation", icon: faNetworkWired },
    { name: "Image and Signal Processing", icon: faEye },
    { name: "Electric Vehicle Charging Control", icon: faChargingStation }
  ];

  const mechProjects = [
    { name: "Manufacturing", icon: faIndustry },
    { name: "Robotics", icon: faRobot },
    { name: "Efficient Energy Utilization", icon: faBolt },
    { name: "Materials", icon: faCogs }
  ];

  const civilProjects = [
    { name: "Structural Engineering", icon: faBuilding },
    { name: "Geotechnical Engineering", icon: faMountain },
    { name: "Environmental Engineering", icon: faSeedling },
    { name: "Transportation Engineering", icon: faRoad },
    { name: "Construction Management", icon: faHardHat },
    { name: "Water Resources Engineering", icon: faWater },
    { name: "Urban Planning", icon: faCity },
    { name: "Building Materials", icon: faHome },
    { name: "Sustainable Design", icon: faSeedling },
    { name: "Structural Health Monitoring", icon: faDraftingCompass }
  ];

  return (
    <div ref={ref} id = 'service' className="relative bg-black py-16 overflow-hidden">
      {/* Fireworks / Crackers background */}
      <motion.div
        className="absolute inset-0 z-0"
        animate={{ opacity: [0.2, 0.5, 0.2] }}
        transition={{ repeat: Infinity, duration: 3 }}
      >
        <div className="w-full h-full bg-[radial-gradient(circle,rgba(255,165,0,0.2)0%,transparent70%)] animate-pulse"></div>
      </motion.div>

      {/* Section Title */}
      <div className="relative z-10 text-center mb-20 px-4">
        <h1 className="text-4xl md:text-6xl font-bold mb-8 text-orange-400">Our Services</h1>
        <p className="text-xl text-orange-300 max-w-4xl mx-auto">
          Explore our diverse range of technological services tailored to tackle complex challenges and foster innovation across multiple engineering domains.
        </p>
      </div>

      {/* Projects Sections */}
      {[
        { title: "Projects for CSE", data: cseProjects },
        { title: "Projects for EEE", data: eeeProjects },
        { title: "Projects for MECH", data: mechProjects },
        { title: "Projects for Civil", data: civilProjects }
      ].map((section, i) => (
        <div key={i} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-center text-orange-400">{section.title}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10">
            {section.data.map((project, index) => (
              <div key={index} className="bg-gradient-to-br from-orange-600 via-yellow-500 to-orange-400 p-8 rounded-xl shadow-2xl transform transition-transform hover:scale-105 hover:shadow-orange-500">
                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                >
                  <FontAwesomeIcon icon={project.icon} className="text-6xl text-white mb-4" />
                </motion.div>
                <h3 className="text-2xl font-semibold text-white">{project.name}</h3>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

export default ServiceSection;
